import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import cn from '@/utils/cn';
import SPAPill from '../../shared/SPAPill';
import DisconnectGoogleButton from './DisconnectGoogleButton';
import { selectionKey } from './UserResultsList';
import type { GoogleConflict, UserSearchDetail } from './types';

const InvalidTokenPill = ({ invalid }: { invalid: boolean | null }) => {
  if (!invalid) return null;

  return <SPAPill tone="error">Token invalid</SPAPill>;
};

const AccountRow = ({ children, label }: { children: React.ReactNode; label: string }) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-py-2 tw-text-sm">
    <span className="tw-w-56 tw-shrink-0 tw-font-bold tw-text-spa-heading">{label}</span>
    <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-2 tw-text-spa-primary">{children}</div>
  </div>
);

const NotLinked = () => <span className="tw-text-spa-secondary">Not linked</span>;

const GoogleConflictCallout = ({ conflicts }: { conflicts: GoogleConflict[] }) => {
  const [searchParams] = useSearchParams();
  const linkTo = (conflict: GoogleConflict) => {
    const next = new URLSearchParams(searchParams);
    next.set('selected', selectionKey(conflict));
    return { hash: '#linked-accounts', search: next.toString() };
  };

  return (
    <div
      className={cn(
        'tw-mt-2 tw-rounded-md tw-border tw-border-solid tw-border-spa-warning tw-px-4 tw-py-3',
        'tw-text-sm tw-text-spa-primary',
      )}
    >
      <p className="tw-m-0 tw-font-semibold tw-text-spa-heading">Google account linked in another district</p>
      <p className="tw-mb-0 tw-mt-1">
        A user with this same username elsewhere holds a Google link. If this user can&rsquo;t
        connect to Google Classroom, the Google account is likely attached to the wrong district.
      </p>
      <ul className="tw-m-0 tw-mt-2 tw-list-none tw-p-0">
        {conflicts.map(conflict => (
          <li
            key={`${conflict.user_type}-${conflict.id}`}
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1"
          >
            <span>
              {conflict.first_name} {conflict.last_name}
              {conflict.subscriber && ` in ${conflict.subscriber.name}`}
              {conflict.google_email && ` — linked to ${conflict.google_email}`}
            </span>
            <Link
              className="tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover"
              to={linkTo(conflict)}
            >
              View account
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface LinkedAccountsSectionProps {
  detail: UserSearchDetail;
}

const LinkedAccountsSection = ({ detail }: LinkedAccountsSectionProps) => (
  <>
    <AccountRow label="Rostering">
      {detail.provider
        ? <span>{detail.provider} — uid: {detail.uid || 'none'}</span>
        : <NotLinked />}
    </AccountRow>
    <AccountRow label="Google">
      {detail.google ? (
        <>
          <span>{detail.google.email || detail.google.display_name || detail.google.gid}</span>
          {detail.google.classroom_authorized && <SPAPill tone="success">Classroom authorized</SPAPill>}
          <InvalidTokenPill invalid={detail.google.invalid_token} />
          <DisconnectGoogleButton detail={detail} />
        </>
      ) : <NotLinked />}
    </AccountRow>
    {detail.google_conflicts.length > 0 && <GoogleConflictCallout conflicts={detail.google_conflicts} />}
    <AccountRow label="LMS launch identity (Canvas/Schoology)">
      {detail.lti_accounts.length > 0 ? (
        <ul className="tw-m-0 tw-list-none tw-p-0">
          {detail.lti_accounts.map(account => (
            <li key={account.id}>
              {account.provider || 'unknown'} — uid: {account.uid} (key #{account.lti_key_id})
            </li>
          ))}
        </ul>
      ) : <NotLinked />}
    </AccountRow>
  </>
);

export default LinkedAccountsSection;
